import * as React from 'react';

import { cva, type VariantProps } from 'class-variance-authority';
import { X } from 'lucide-react';
import {
  Dialog as AriaDialog,
  DialogProps as AriaDialogProps,
  DialogRenderProps,
  DialogTrigger as AriaDialogTrigger,
  Heading as AriaHeading,
  HeadingProps as AriaHeadingProps,
  Modal as AriaModal,
  ModalOverlay as AriaModalOverlay,
  ModalOverlayProps as AriaModalOverlayProps,
  composeRenderProps,
} from 'react-aria-components';

import { useScrollVShadow } from '../../../hooks';
import { cn } from '../../../lib/stylingUtils';
import { Button } from '../buttons/Button';
import { Loading } from '../feedback/Loading';

const Dialog = AriaDialog;

const DialogTrigger = AriaDialogTrigger;

const dialogVariants = cva(
  [
    'fixed z-50 gap-4 overflow-hidden bg-white text-neutral/90',
    'border-neutral/20 transition ease-in-out',
    /* Entering */
    'rac-entering:animate-in rac-entering:duration-150',
    /* Exiting */
    'rac-exiting:animate-out rac-exiting:duration-150',
  ],
  {
    variants: {
      position: {
        fixed: [
          'absolute top-[20vh] left-[50vw] z-50 w-full max-w-lg -translate-x-1/2 border-0 shadow-lg',
          'duration-150 rac-entering:animate-in rac-exiting:animate-out rac-exiting:duration-150',
          /* Entering */
          'rac-entering:fade-in-0 rac-entering:zoom-in-95',
          /* Exiting */
          'rac-exiting:fade-out-0 rac-exiting:zoom-out-95',
          /* Screen Sizes */
          'sm:rounded-lg md:w-full',
        ],
        center: [
          'z-50 w-auto max-w-[50w] border-0 shadow-lg',
          'duration-150 rac-entering:animate-in rac-exiting:animate-out rac-exiting:duration-150',
          /* Entering */
          'rac-entering:fade-in-0 rac-entering:zoom-in-95',
          /* Exiting */
          'rac-exiting:fade-out-0 rac-exiting:zoom-out-95',
          /* Screen Sizes */
          'sm:rounded-lg',
        ],
        top: [
          'inset-x-0 top-0 w-full border-b',
          'rac-entering:slide-in-from-top rac-exiting:slide-out-to-top',
        ],
        bottom: [
          'inset-x-0 bottom-0 h-[87.5%] w-full border-t shadow-[0_0_15px_rgba(0,0,0,0.1)]',
          'rac-entering:slide-in-from-bottom rac-exiting:slide-out-to-bottom',
        ],
        left: [
          'inset-y-0 left-0 h-full w-sm border-r',
          'rac-entering:slide-in-from-left rac-exiting:slide-out-to-left',
        ],
        right: [
          'inset-y-0 top-[2.5rem] right-[2.5rem] bottom-[2.5rem] h-[calc(100vh-5rem)] rounded-lg border-t shadow-[0_0_15px_rgba(0,0,0,0.1)]',
          // 'rac-entering:slide-in-from-right rac-exiting:slide-out-to-right',
        ],
      },
      size: {
        wrap: '',
        fill: 'h-[calc(100vh-100px)] w-[calc(100vw-100px)] max-w-[1280px]',
      },
    },
    defaultVariants: {
      position: 'center',
    },
  },
);

const dialogContentVariants = cva(
  '@DialogContent flex h-full min-h-0 flex-col outline-none',
  {
    variants: {
      layout: {
        default: '',
        grid: 'grid h-full gap-0',
      },
    },
    defaultVariants: {
      layout: 'default',
    },
  },
);

const scrollShadowVariants = cva(
  'pointer-events-none absolute inset-x-0 h-3 transition-opacity duration-150',
  {
    variants: {
      direction: {
        top: 'top-0 bg-gradient-to-b from-neutral/10 to-transparent',
        bottom: 'bottom-0 bg-gradient-to-t from-neutral/10 to-transparent',
      },
      visible: {
        true: 'opacity-100',
        false: 'opacity-0',
      },
    },
    defaultVariants: {
      visible: false,
    },
  },
);

/**
 * Overlay component for dialogs.
 * Renders a backdrop behind the dialog content.
 *
 * @param props - Dialog overlay properties
 */
const DialogOverlay = ({
  className,
  ref,
  ...props
}: AriaModalOverlayProps & {
  ref?: React.RefObject<HTMLDivElement | null>;
}) => (
  <AriaModalOverlay
    ref={ref}
    className={composeRenderProps(
      cn('@DialogOverlay', className),
      (className) =>
        cn(
          'group/dialog-overlay fixed inset-0 z-50 flex items-center justify-center bg-[#00000055]',
          /* Exiting */
          'rac-exiting:animate-out rac-exiting:duration-150 rac-exiting:fade-out-0',
          /* Entering */
          'rac-entering:animate-in rac-entering:fade-in-0',
          'pointer-events-auto!',
          className,
        ),
    )}
    {...props}
  />
);

/**
 * Props for the DialogContent component.
 */
interface DialogContentProps
  extends
    Omit<React.ComponentProps<typeof AriaModal>, 'children'>,
    VariantProps<typeof dialogVariants> {
  /**
   * Content to display within the dialog
   */
  children?: AriaDialogProps['children'];

  /**
   * ARIA role for the dialog
   */
  role?: AriaDialogProps['role'];

  /**
   * Whether to show a close button in the top-right corner
   * @default true
   */
  closeButton?: boolean;

  /**
   * Additional className for the inner dialog element
   */
  dialogClassName?: string;

  /**
   * Whether to show a loading indicator
   */
  isLoading?: boolean;

  /**
   * Function to call before closing the dialog
   */
  onBeforeClose?: () => { shouldClose: boolean };

  size?: 'wrap' | 'fill';

  'aria-label'?: string;
}

/**
 * Main content container for dialogs.
 * Renders the dialog with the specified positioning and styling.
 *
 * @example
 * ```tsx
 * <DialogTrigger>
 *   <Button>Open Dialog</Button>
 *   <DialogContent>
 *     <DialogHeader>
 *       <DialogTitle>Dialog Title</DialogTitle>
 *       <DialogDescription>This is a description of the dialog.</DialogDescription>
 *     </DialogHeader>
 *     <div>Dialog content goes here</div>
 *     <DialogFooter>
 *       <Button>Save Changes</Button>
 *     </DialogFooter>
 *   </DialogContent>
 * </DialogTrigger>
 * ```
 *
 * @param props - Dialog content properties
 */
const DialogContent = ({
  className,
  children,
  position = 'center',
  role,
  closeButton = true,
  dialogClassName,
  isLoading,
  onBeforeClose,
  onOpenChange,
  size = 'wrap',
  'aria-label': ariaLabel,
  ...props
}: DialogContentProps) => (
  <AriaModal
    className={composeRenderProps(
      cn(
        '@DialogContent border border-abs-white/30 bg-white',
        position === 'center' && 'relative',
        className,
      ),
      (className) => cn(dialogVariants({ position, size: size }), className),
    )}
    {...props}
  >
    <AriaDialog
      role={role}
      aria-label={ariaLabel}
      className={cn(
        dialogContentVariants({
          layout: position !== 'center' ? 'grid' : 'default',
        }),
        dialogClassName,
      )}
    >
      {composeRenderProps(
        children,
        (children, renderProps: DialogRenderProps) => (
          <Loading isLoading={isLoading} className="p-4">
            {children}
            {closeButton && (
              <Button
                onPress={() => {
                  const { shouldClose } = onBeforeClose?.() ?? {
                    shouldClose: true,
                  };
                  if (shouldClose) {
                    renderProps.close();
                    onOpenChange?.(false);
                  }
                }}
                variant="text"
                color="neutral"
                size="icon"
                icon={<X />}
                className="absolute top-4 right-4 px-1 py-2 rac-disabled:pointer-events-none"
              >
                <span className="sr-only">Close</span>
              </Button>
            )}
          </Loading>
        ),
      )}
    </AriaDialog>
  </AriaModal>
);

/**
 * Header component for dialogs.
 * Provides consistent styling for the top section of a dialog.
 * Typically contains DialogTitle and DialogDescription.
 *
 * @param props - Dialog header properties
 */
const DialogHeader = ({
  className,
  ...props
}: AriaHeadingProps & {
  className?: string;
  children?: React.ReactNode;
  slot?: string;
}) => (
  <AriaHeading
    className={cn(
      '@DialogHeader',
      'mr-10 flex flex-col space-y-1.5 p-4 text-center sm:text-left',
      className,
    )}
    slot="title"
    {...props}
  />
);

const DialogMain = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => {
  const ref = React.useRef<HTMLDivElement | null>(null);
  const { showTopShadow, showBottomShadow } = useScrollVShadow(ref);

  return (
    <div className="@DialogMain relative flex-1 overflow-hidden">
      <div
        ref={ref}
        className={cn('h-full overflow-y-auto p-4', className)}
        {...props}
      />
      {/* Top shadow */}
      <div
        className={scrollShadowVariants({
          direction: 'top',
          visible: showTopShadow,
        })}
      />
      {/* Bottom shadow */}
      <div
        className={scrollShadowVariants({
          direction: 'bottom',
          visible: showBottomShadow,
        })}
      />
    </div>
  );
};

/**
 * Footer component for dialogs.
 * Provides consistent styling for the bottom section of a dialog.
 * Typically contains action buttons.
 *
 * @param props - Dialog footer properties
 */
const DialogFooter = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn(
      '@DialogFooter',
      'flex flex-col-reverse sm:flex-row sm:justify-end sm:space-x-2',
      'border-t border-foreground/10 bg-foreground/5 p-4',
      className,
    )}
    {...props}
  />
);

/**
 * Title component for dialogs.
 * Renders as a heading element with the appropriate ARIA attributes.
 *
 * @param props - Dialog title properties
 */
const DialogTitle = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLSpanElement>) => (
  <span
    className={cn(
      '@DialogTitle',
      'text-lg leading-none font-semibold tracking-tight',
      className,
    )}
    {...props}
  />
);

/**
 * Description component for dialogs.
 * Provides additional context about the dialog's purpose.
 *
 * @param props - Dialog description properties
 */
const DialogDescription = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLSpanElement>) => (
  <span
    className={cn(
      '@DialogDescription',
      'flex flex-col space-y-1.5 text-center sm:text-left',
      className,
    )}
    {...props}
  />
);

export {
  Dialog,
  DialogOverlay,
  DialogTrigger,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogMain,
  DialogFooter,
  DialogTitle,
};
export type { DialogContentProps };
